import { Component, effect, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService, SortEvent } from 'primeng/api';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { PaginatorState } from 'primeng/paginator';
import { Observable, Subject, catchError, finalize, map, of, throwError } from 'rxjs';
import { EnumValues, DynamicFilterConfig, DynamicFilterValues, createDynamicFilterState } from '@card-software/library';
import { AuthService, SiteService } from '@card-software/nexus-utilities';
import { Business, BusinessEntityType } from '@app/models/finance/business';
import { BusinessSearchCommand } from '@app/models/business-search';
import { PurchaseOrderStatus, PurchaseOrderType } from '@app/models/purchase-order/enum-purchase';
import { PurchaseOrder, PurchaseOrderSearchCommand } from '@app/models/purchase-order/purchase-order';
import { AdministrativePurchaseOrderRoles, PurchaseOrderRoles } from '@app/models/roles/roles-group';
import { SITE_ID_PRIDE_CHEMICALS, SITE_ID_SIERRA } from '@app/models/site/site-ids';
import { BusinessProxyService } from '@app/proxies/finance/business.proxy.service';
import { PurchaseOrderProxyService } from '@app/proxies/order/purchase-order.proxy.service';
import { EnumService } from '@app/services/enum.service';

// Site → allowed PurchaseOrderType list. Centralized so adding a new site is a one-line edit.
// Sites not listed here fall back to ALLOWED_ORDER_TYPES_DEFAULT (Standard only).
const ALLOWED_ORDER_TYPES_BY_SITE: Record<string, PurchaseOrderType[]> = {
  [SITE_ID_SIERRA]: [PurchaseOrderType.Standard, PurchaseOrderType.Yarn],
  [SITE_ID_PRIDE_CHEMICALS]: [PurchaseOrderType.Standard, PurchaseOrderType.Chemicals],
};

const ALLOWED_ORDER_TYPES_DEFAULT: PurchaseOrderType[] = [PurchaseOrderType.Standard];

@Component({
  selector: 'app-purchase-order',
  templateUrl: './purchase-order.component.html',
  styleUrl: './purchase-order.component.scss',
})
export class PurchaseOrderComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private enumService = inject(EnumService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  private purchaseOrderService = inject(PurchaseOrderProxyService);
  private businessService = inject(BusinessProxyService);

  isLoading = signal(0);
  listPurchaseOrders = signal<PurchaseOrder[]>([]);
  statuses = signal<EnumValues>({});
  /** Businesses named on the current page, by id. Fetched per page through batch/ids, never the whole catalogue. */
  pageBusinesses = signal<Map<string, Business>>(new Map());
  /** Suggestions for the Provider / Bill To / Ship To filters, searched server-side as the user types. */
  filteredProviders = signal<Business[]>([]);
  private latestProviderQuery = '';
  enumsReady = signal(false);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  orderTypes = signal<EnumValues>({});
  private readonly filterConfigWithOptions = computed(() =>
    this.enumsReady() && Object.keys(this.orderTypes()).length > 0 ? this.filterConfig() : null,
  );
  readonly filterState = createDynamicFilterState({ storageKey: 'purchase-order-filters', config: this.filterConfigWithOptions });
  showFilters = signal(false);
  currentPage = signal<number>(1);
  totalRecords = signal<number>(0);
  pageSize = signal<number>(25);
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  /** Administrative orders (#5942) exist only for role holders: the API hides them, the UI hides the option. */
  canSeeAdministrative = computed(() => AuthService.hasRole(AdministrativePurchaseOrderRoles.AdministrativePurchaseOrderAdmin));

  /** Admin/Editor create the regular types; the Administrative role creates Administrative ones on its own (#5942). */
  canEditRegular = computed(
    () => AuthService.hasRole(PurchaseOrderRoles.PurchaseOrderAdmin) || AuthService.hasRole(PurchaseOrderRoles.PurchaseOrderEditor),
  );

  canEdit = computed(() => this.canEditRegular() || this.canSeeAdministrative());

  private destroy$ = new Subject<void>();
  private filtersPanelInitialized = false;
  private lastSearchCriteria: PurchaseOrderSearchCommand = {};
  orderMenuItems = signal<MenuItem[]>([]);
  selectedOrderType = signal<number | null>(null);

  getRowData(rowData: unknown): PurchaseOrder {
    return rowData as PurchaseOrder;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('PurchaseOrderStatus', 'orderEnums').values;
        if (Object.keys(statusesValue).length > 0) {
          this.statuses.set(statusesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const orderTypesValue = this.enumService.getEnum('PurchaseOrderType', 'orderEnums').values;
        if (Object.keys(orderTypesValue).length > 0) {
          this.orderTypes.set(orderTypesValue);
          this.setOrderTypeMenu();
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translate.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.tryInitializeFilterConfig();
  }

  /**
   * Sets up the SpeedDial menu items for order type selection.
   * Filters PurchaseOrderType entries by the user's currently selected site
   * using ALLOWED_ORDER_TYPES_BY_SITE; unmapped sites fall back to the default list
   * so the menu always has at least one option.
   */
  setOrderTypeMenu(): void {
    const allowedTypes = this.canEditRegular()
      ? [...(ALLOWED_ORDER_TYPES_BY_SITE[this.selectedSiteId] ?? ALLOWED_ORDER_TYPES_DEFAULT)]
      : [];
    if (this.canSeeAdministrative()) {
      allowedTypes.push(PurchaseOrderType.Administrative);
    }
    const labels = this.orderTypes();

    const menuItems: MenuItem[] = allowedTypes
      .filter((type) => labels[type] !== undefined)
      .map((type) => ({
        label: labels[type],
        command: () => {
          this.selectedOrderType.set(type);
          this.redirectToDetails('new', true);
        },
      }));

    this.orderMenuItems.set(menuItems);
  }

  get selectedSiteId(): string {
    return SiteService.getSelectedSiteId() || '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translate.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translate.instant(key);
      return translation === key ? fallback : translation;
    };

    const statusOptions = Object.entries(this.statuses()).map(([key, value]) => ({
      value: parseInt(key),
      label: value,
    }));

    const typeOptions = Object.entries(this.orderTypes())
      .map(([key, value]) => ({
        value: parseInt(key),
        label: value,
      }))
      .filter((option) => option.value !== PurchaseOrderType.Administrative || this.canSeeAdministrative());

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'purchaseOrderNumber',
          label: translate('Purchase_Order_PO_Number', 'PO Number'),
          placeholder: translate('Purchase_Order_Order_Id', 'Order Id'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'providerReference',
          label: translate('Purchase_Order_Provider', 'Provider'),
          placeholder: translate('Search_Business', 'Search Business'),
          autocompleteConfig: {
            suggestions: this.filteredProviders(),
            optionLabel: 'name',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterProviders(event),
            resolveById: (id: string) => this.resolveBusiness(id),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'autocomplete',
          formControlName: 'billToReference',
          label: translate('Purchase_Order_BillingTo', 'Billing To'),
          placeholder: translate('Search_Business', 'Search Business'),
          autocompleteConfig: {
            suggestions: this.filteredProviders(),
            optionLabel: 'name',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterProviders(event),
            resolveById: (id: string) => this.resolveBusiness(id),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'autocomplete',
          formControlName: 'shipToReference',
          label: translate('Purchase_Order_ShippingTo', 'Shipping To'),
          placeholder: translate('Search_Business', 'Search Business'),
          autocompleteConfig: {
            suggestions: this.filteredProviders(),
            optionLabel: 'name',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterProviders(event),
            resolveById: (id: string) => this.resolveBusiness(id),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'text',
          formControlName: 'itemCodes',
          label: translate('Purchase_Order_Details_Item_Code', 'Item Code'),
          placeholder: translate('Purchase_Order_Details_Item_Code', 'Item Code'),
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'multiselect',
          formControlName: 'status',
          label: translate('Purchase_Order_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: statusOptions,
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'select',
          formControlName: 'type',
          label: translate('Purchase_Order_Type', 'Order Type'),
          placeholder: translate('Select_type', 'Select type'),
          options: typeOptions,
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private resolveBusiness(id: string): Observable<Business | null> {
    return this.businessService.getById(id).pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  filterProviders(event: AutoCompleteCompleteEvent): void {
    const query = event.query?.trim() ?? '';
    this.latestProviderQuery = query;

    if (!query) {
      this.setProviderSuggestions([]);
      return;
    }

    const searchCommand: BusinessSearchCommand = {
      name: query,
      code: query,
      businessTypes: [BusinessEntityType.Provider, BusinessEntityType.InternalCompany],
    };
    this.businessService.search(searchCommand, true).subscribe({
      next: (response) => {
        // Typing fires a search per keystroke; only the answer to the latest query may land.
        if (query === this.latestProviderQuery) {
          this.setProviderSuggestions(response.data?.items ?? []);
        }
      },
      error: () => this.setProviderSuggestions([]),
    });
  }

  private setProviderSuggestions(businesses: Business[]): void {
    this.filteredProviders.set(businesses);
    this.updateAutocompleteSuggestions('providerReference', businesses);
    this.updateAutocompleteSuggestions('billToReference', businesses);
    this.updateAutocompleteSuggestions('shipToReference', businesses);
  }

  /** Names the businesses on this page only: one batch/ids call for the ids the page actually shows. */
  private loadPageBusinesses(orders: PurchaseOrder[]): void {
    const ids = [
      ...new Set(orders.flatMap((po) => [po.providerReference, po.billToReference, po.shipToReference]).filter((id): id is string => !!id)),
    ];
    if (ids.length === 0) {
      this.pageBusinesses.set(new Map());
      return;
    }

    this.startLoading();
    this.businessService
      .getByIds(ids)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.pageBusinesses.set(new Map((response.data ?? []).map((business) => [business.id, business]))),
        error: () => this.pageBusinesses.set(new Map()),
      });
  }

  private updateAutocompleteSuggestions(formControlName: string, suggestions: unknown[]): void {
    const currentConfig = this.filterConfig();
    if (!currentConfig) return;
    const updatedFilters = currentConfig.filters.map((filter) => {
      if (filter.formControlName === formControlName && filter.autocompleteConfig) {
        return { ...filter, autocompleteConfig: { ...filter.autocompleteConfig, suggestions } };
      }
      return filter;
    });
    this.filterConfig.set({ ...currentConfig, filters: updatedFilters });
  }

  private onSearch(searchCriteria: PurchaseOrderSearchCommand): void {
    this.startLoading();
    this.lastSearchCriteria = searchCriteria;
    this.purchaseOrderService
      .searchPurchaseOrder(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const pagedResult = response.data;
          this.listPurchaseOrders.set(pagedResult?.items ?? []);
          this.totalRecords.set(pagedResult?.totalCount ?? 0);
          this.loadPageBusinesses(pagedResult?.items ?? []);
        },
        error: () => {
          this.listPurchaseOrders.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = this.normalizeSortField(event.field, this.sortField());
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const searchCriteria: PurchaseOrderSearchCommand = {
      purchaseOrderNumber: (filters['purchaseOrderNumber'] as string)?.trim() || undefined,
      providerReference: filters['providerReference']
        ? typeof filters['providerReference'] === 'object'
          ? (filters['providerReference'] as Record<string, string>)['id']
          : (filters['providerReference'] as string)
        : undefined,
      billToReference: filters['billToReference']
        ? typeof filters['billToReference'] === 'object'
          ? (filters['billToReference'] as Record<string, string>)['id']
          : (filters['billToReference'] as string)
        : undefined,
      shipToReference: filters['shipToReference']
        ? typeof filters['shipToReference'] === 'object'
          ? (filters['shipToReference'] as Record<string, string>)['id']
          : (filters['shipToReference'] as string)
        : undefined,
      itemCodes: (filters['itemCodes'] as string)?.trim()
        ? (filters['itemCodes'] as string)
            .split(',')
            .map((code: string) => code.trim())
            .filter(Boolean)
        : undefined,
      status:
        Array.isArray(filters['status']) && (filters['status'] as unknown[]).length > 0
          ? (filters['status'] as unknown[]).map((item: unknown) =>
              typeof item === 'object' && item !== null ? parseInt((item as Record<string, string>)['value']) : parseInt(String(item)),
            )
          : undefined,
      type:
        filters['type'] != null
          ? typeof filters['type'] === 'object'
            ? parseInt((filters['type'] as Record<string, string>)['value'])
            : parseInt(filters['type'] as string)
          : undefined,
    };

    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  public redirectToDetails(param?: string, orderTypeActive = false): void {
    const queryParams = orderTypeActive
      ? { id: param ? param : 'new', orderType: this.selectedOrderType() }
      : { id: param ? param : 'new' };
    this.router.navigate(['/purchase-order-details'], { queryParams });
  }

  getSpecificProvider(code: string): string {
    if (!code) {
      return '---------';
    }

    const provider = this.pageBusinesses().get(code);
    return provider ? `${provider.name} (${provider.code})` : '---------';
  }

  getSpecificBillTo(code: string): string {
    if (!code) {
      return '---------';
    }
    const bill = this.pageBusinesses().get(code);
    return bill ? bill.name : '---------';
  }

  getSpecificShippingTo(code: string): string {
    if (!code) {
      return '---------';
    }

    const shipping = this.pageBusinesses().get(code);
    return shipping ? shipping.name : '---------';
  }

  canPrintReport(po: PurchaseOrder): boolean {
    // Standard and Administrative POs only print once Approved or higher (not Draft, Submitted, Cancelled, or Rejected)
    if (po.type === PurchaseOrderType.Standard || po.type === PurchaseOrderType.Administrative) {
      const nonPrintableStatuses = [
        PurchaseOrderStatus.Draft,
        PurchaseOrderStatus.Submitted,
        PurchaseOrderStatus.Cancelled,
        PurchaseOrderStatus.Rejected,
      ];
      return !nonPrintableStatuses.includes(po.status);
    }
    // For other types (Yarn), allow printing
    return true;
  }

  printReportPdf(purchaseOrderId?: string): void {
    if (!purchaseOrderId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        key: 'br',
        detail: 'Purchase Order ID is required',
      });
      return;
    }

    this.startLoading();
    this.purchaseOrderService
      .downloadReport(purchaseOrderId)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 1000);
        },
      });
  }

  private normalizeSortField(sortField: string | string[] | undefined | null, fallback: string | undefined): string | undefined {
    if (Array.isArray(sortField)) {
      return sortField[0] ?? fallback;
    }
    return sortField ?? fallback;
  }
}
